import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  ListBulletIcon,
  VideoCameraIcon,
  PhoneIcon,
  MapPinIcon,
  ClockIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

import ScheduleListView, { Appointment } from './ScheduleListView';
import { useGuideTimezone } from '../hooks/useGuideTimezone';
import { formatTimeInTimezone, formatDateInTimezone, formatLegacySessionTime, getTimezoneAbbreviation } from '../utils/timezoneUtils';
import WorkingHoursEditor from '../components/WorkingHoursEditor';
import { AddToCalendarButton } from '../components/AddToCalendarButton';
import { getSupabaseClient } from '../lib/supabase';

type CalendarFilter = 'month' | 'week' | 'today';

const SchedulePage: React.FC = () => {
  const navigate = useNavigate();
  const { timezone: guideTimezone } = useGuideTimezone();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [calendarFilter, setCalendarFilter] = useState<CalendarFilter>('month');
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showWorkingHours, setShowWorkingHours] = useState(false);
  const [calendarConnected, setCalendarConnected] = useState<boolean | null>(null);
  const [calendarAlertDismissed, setCalendarAlertDismissed] = useState(false);
  const [confirmingSessionId, setConfirmingSessionId] = useState<string | null>(null);

  // Check if user has connected a calendar
  const checkCalendarConnection = useCallback(async () => {
    try {
      const supabase = getSupabaseClient();
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        return;
      }

      // Check Google Calendar
      const { data: googleData } = await supabase
        .from('guide_calendars')
        .select('id')
        .eq('user_id', session.user.id)
        .eq('provider', 'google')
        .limit(1)
        .maybeSingle();

      if (googleData) {
        setCalendarConnected(true);
        return;
      }

      // Check Microsoft Outlook
      const { data: msData } = await supabase
        .from('user_identities')
        .select('id')
        .eq('user_id', session.user.id)
        .eq('provider', 'microsoft')
        .limit(1)
        .maybeSingle();

      if (msData) {
        setCalendarConnected(true);
        return;
      }

      setCalendarConnected(false);
    } catch (error) {
      console.error('Error checking calendar connection:', error);
    }
  }, []);

  useEffect(() => {
    checkCalendarConnection();
  }, [checkCalendarConnection]);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const supabase = getSupabaseClient();
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        setAppointments([]);
        setLoading(false);
        return;
      }
      // Note: scheduled_by column may not exist until migration is run - using * to be safe
      const { data: sessions, error: sessionsError } = await supabase
        .from('scheduled_sessions')
        .select('*')
        .eq('guide_id', user.id);
      if (sessionsError || !sessions) {
        setAppointments([]);
        setLoading(false);
        return;
      }

      const seekerUserIds = Array.from(
        new Set(
          sessions
            .map((row: any) => row.seeker_id)
            .filter((value: any) => typeof value === 'string' && value.trim().length > 0)
        )
      ) as string[];

      const profileLookup = new Map<string, string | null>();
      if (seekerUserIds.length > 0) {
        const { data: profileRows, error: profileError } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', seekerUserIds);

        if (profileError) {
          console.warn('Unable to load seeker profiles', profileError);
        } else if (profileRows) {
          profileRows.forEach((row: any) => {
            if (row.id) {
              profileLookup.set(String(row.id), row.full_name ?? null);
            }
          });
        }
      }

      const mapped = sessions.map((row: any) => {
        // Map session_type to Appointment type
        let type: Appointment['type'] = 'video_call';
        if (row.session_type && row.session_type.toLowerCase().includes('phone')) type = 'phone_call';
        else if (row.session_type && row.session_type.toLowerCase().includes('in_person')) type = 'in_person';
        const seekerUserId = row.seeker_id ? String(row.seeker_id) : null;
        const profileName = seekerUserId ? profileLookup.get(seekerUserId) ?? null : null;
        const client_name = profileName || row.seeker_name || 'Seeker';

        // NEW: Prefer start_time (UTC) for date sorting, but keep as Date object for compatibility
        let date: Date;
        if (row.start_time) {
          // Use UTC timestamp
          date = new Date(row.start_time);
        } else {
          // Legacy: compose from session_date and session_time
          // Append T00:00:00 to avoid date-only strings being parsed as UTC midnight
          date = new Date(row.session_date + 'T00:00:00');
          if (row.session_time) {
            const [h, m, s] = row.session_time.split(':');
            date.setHours(Number(h), Number(m), Number(s || 0));
          }
        }

        return {
          id: row.id,
          client_name,
          title: row.session_type ? row.session_type.charAt(0).toUpperCase() + row.session_type.slice(1) : 'Session',
          type,
          date, // This is UTC Date object for sorting
          duration: row.duration_minutes || 30,
          status: (row.session_status as Appointment['status']) || 'scheduled',
          scheduled_by: row.scheduled_by || null,
          notes: row.notes || '',
          client_email: undefined,
          client_avatar: undefined,
        };
      });
      setAppointments(mapped);
    } catch (err) {
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Refetch sessions when the tab regains focus (handles cross-user reschedules)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchSessions();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchSessions]);

  // Function to confirm a session via edge function
  const handleConfirmSession = async (sessionId: string) => {
    if (confirmingSessionId) return; // Prevent duplicate clicks
    setConfirmingSessionId(sessionId);
    try {
      const supabase = getSupabaseClient();
      const { data: { session: authSession }, error: authError } = await supabase.auth.getSession();

      if (authError || !authSession?.access_token) {
        alert('Please log in to confirm sessions.');
        return;
      }

      const response = await fetch(
        `${(window as any).__ENV__?.SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL}/functions/v1/confirm-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authSession.access_token}`,
          },
          body: JSON.stringify({ session_id: sessionId }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to confirm session:', errorData);
        alert(errorData.error || 'Failed to confirm session. Please try again.');
        return;
      }

      // Update the local state to reflect the change
      setAppointments(prev => prev.map(apt =>
        apt.id === sessionId
          ? { ...apt, status: 'confirmed' as const, scheduled_by: null }
          : apt
      ));

      console.log('Session confirmed via Edge Function');
    } catch (err) {
      console.error('Error confirming session:', err);
      alert('Failed to confirm session. Please try again.');
    } finally {
      setConfirmingSessionId(null);
    }
  };

  const generateAvatar = (name: string) => {
    const safeName = (name || '').trim();
    const segments = safeName ? safeName.split(/\s+/) : [];
    const initials = segments
      .slice(0, 2)
      .map((segment) => (segment ? segment[0].toUpperCase() : ''))
      .join('') || '?';
    const colors = [
      'bg-[#2C2731]',
      'bg-[#3A3340]',
      'bg-[#4B4353]',
      'bg-[#352F3B]',
      'bg-[#433B4A]',
      'bg-[#2E2836]'
    ];
    const colorIndex = safeName.length % colors.length;

    return (
      <div className={`w-8 h-8 rounded-full ${colors[colorIndex]} flex items-center justify-center text-[#FFFCEF] text-xs font-medium border border-[#FFFCEF]`}>
        {initials}
      </div>
    );
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'video_call':
        return <VideoCameraIcon className="w-3 h-3" />;
      case 'phone_call':
        return <PhoneIcon className="w-3 h-3" />;
      case 'in_person':
        return <MapPinIcon className="w-3 h-3" />;
      default:
        return <CalendarIcon className="w-3 h-3" />;
    }
  };

  const formatTime = (date: Date) => {
    // Convert Date object to ISO string and format in guide timezone
    const isoString = date.toISOString();
    const time = formatTimeInTimezone(isoString, guideTimezone);
    const tz = getTimezoneAbbreviation(isoString, guideTimezone);
    return tz ? `${time} ${tz}` : time;
  };

  const formatDate = (date: Date) => {
    // Convert Date object to ISO string and format in guide timezone
    const isoString = date.toISOString();
    return formatDateInTimezone(isoString, guideTimezone);
  };

  const canEnterWaitingRoom = (appointment: Appointment) => {
    return !['completed', 'cancelled', 'no_show'].includes(appointment.status);
  };

  const getTodayAppointments = () => {
    const today = new Date();
    // Remove time part for comparison
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return appointments.filter(apt => {
      const aptDate = new Date(apt.date.getFullYear(), apt.date.getMonth(), apt.date.getDate());
      return aptDate.getTime() === todayDate.getTime();
    }).sort((a, b) => a.date.getTime() - b.date.getTime());
  };

  const getUpcomingWeekAppointments = () => {
    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);

    return appointments.filter(apt => 
      apt.date > today && apt.date <= nextWeek
    ).sort((a, b) => a.date.getTime() - b.date.getTime());
  };

  const getMonthName = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getAppointmentsForDate = (date: Date) => {
    return appointments.filter(apt => 
      apt.date.toDateString() === date.toDateString()
    );
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() + 7);
    }
    setCurrentDate(newDate);
  };

  const navigateDay = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 1);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const navigate_calendar = (direction: 'prev' | 'next') => {
    switch (calendarFilter) {
      case 'today':
        navigateDay(direction);
        break;
      case 'week':
        navigateWeek(direction);
        break;
      default:
        navigateMonth(direction);
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setCalendarFilter('today');
  };

  const getWeekDates = (date: Date) => {
    const day = date.getDay();
    const start = new Date(date);
    start.setDate(date.getDate() - day);
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      dates.push(d);
    }
    return dates;
  };

  const getWeekRange = (date: Date) => {
    const weekDates = getWeekDates(date);
    const start = weekDates[0];
    const end = weekDates[6];
    const startMonth = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endMonth = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startMonth} - ${endMonth}`;
  };

  const getHeaderText = () => {
    switch (calendarFilter) {
      case 'today':
        return currentDate.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        });
      case 'week':
        return getWeekRange(currentDate);
      default:
        return getMonthName(currentDate);
    }
  };

  const getAppointmentsForDay = (date: Date) => {
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);

    return appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return aptDate >= dayStart && aptDate < dayEnd;
    }).sort((a, b) => a.date.getTime() - b.date.getTime());
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const today = new Date();
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-14 sm:h-20 border border-[#2C2731]/20 bg-[#FFFCEF]"></div>);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dayAppointments = getAppointmentsForDate(date);
      const dayDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const isToday = dayDate.getTime() === todayDate.getTime();

      days.push(
        <div key={day} className={`h-14 sm:h-20 border border-[#2C2731]/30 p-0.5 sm:p-1 ${isToday ? 'bg-[#E8DFBD]' : 'bg-[#FFFCEF]'}`}>
          <div className={`text-xs sm:text-sm font-medium mb-0.5 sm:mb-1 ${isToday ? 'text-[#2C2731]' : 'text-[#2C2731]'}`}>
            {day}
          </div>
          <div className="space-y-0.5 sm:space-y-1">
            {/* On mobile, show dots for appointments instead of text */}
            <div className="sm:hidden flex flex-wrap gap-0.5">
              {dayAppointments.slice(0, 3).map((apt) => {
                const joinable = canEnterWaitingRoom(apt);
                return (
                  <div
                    key={apt.id}
                    className={`w-2 h-2 rounded-full ${joinable ? 'bg-[#2C2731]' : 'bg-[#2C2731]/30'}`}
                  />
                );
              })}
              {dayAppointments.length > 3 && (
                <span className="text-[8px] text-[#2C2731]/70">+{dayAppointments.length - 3}</span>
              )}
            </div>
            {/* On desktop, show appointment titles */}
            <div className="hidden sm:block space-y-1">
              {dayAppointments.slice(0, 2).map((apt) => {
                const joinable = canEnterWaitingRoom(apt);
                return (
                  <button
                    key={apt.id}
                    onClick={() => {
                      if (joinable) {
                        navigate(`/waiting-room?sessionId=${apt.id}`);
                      }
                    }}
                    disabled={!joinable}
                    className={`text-xs px-1 py-0.5 rounded truncate text-left w-full transition-colors border ${
                      joinable
                        ? 'bg-[#2C2731] text-[#FFFCEF] hover:bg-[#241F2A] cursor-pointer border-[#2C2731]'
                        : 'bg-[#E8DFBD] text-[#2C2731]/70 cursor-not-allowed border-[#2C2731]/30'
                    }`}
                  >
                    {apt.title}
                  </button>
                );
              })}
              {dayAppointments.length > 2 && (
                <div className="text-xs text-[#2C2731]/70">+{dayAppointments.length - 2} more</div>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    return days;
  };

  const renderWeekView = () => {
    const weekDates = getWeekDates(currentDate);
    const today = new Date();
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    return (
      <div className="p-2 sm:p-4">
        {/* Mobile: Vertical list layout */}
        <div className="sm:hidden space-y-3">
          {weekDates.map((date, index) => {
            const dayAppointments = getAppointmentsForDay(date);
            const dayDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            const isToday = dayDate.getTime() === todayDate.getTime();

            return (
              <div
                key={index}
                className={`rounded-lg border ${isToday ? 'border-[#2C2731] bg-[#E8DFBD]' : 'border-[#2C2731]/30 bg-[#FFFCEF]'}`}
              >
                <div className={`px-3 py-2 border-b ${isToday ? 'border-[#2C2731]/30' : 'border-[#2C2731]/20'}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#2C2731]">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                    </span>
                    <span className={`text-lg font-semibold ${isToday ? 'text-[#2C2731]' : 'text-[#2C2731]/80'}`}>
                      {date.getDate()}
                    </span>
                  </div>
                </div>
                <div className="p-2 space-y-2">
                  {dayAppointments.length > 0 ? (
                    dayAppointments.map((apt) => {
                      const joinable = canEnterWaitingRoom(apt);
                      return (
                        <button
                          key={apt.id}
                          onClick={() => joinable && navigate(`/waiting-room?sessionId=${apt.id}`)}
                          disabled={!joinable}
                          className={`w-full text-left p-2 rounded-md text-xs transition-colors ${
                            joinable
                              ? 'bg-[#2C2731] text-[#FFFCEF]'
                              : 'bg-[#F3EBCD] text-[#2C2731]/70'
                          }`}
                        >
                          <div className="font-medium">{apt.title}</div>
                          <div className="opacity-80">{formatTime(apt.date)} · {apt.client_name}</div>
                        </button>
                      );
                    })
                  ) : (
                    <div className="text-xs text-[#2C2731]/50 text-center py-2">No appointments</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop: Grid layout */}
        <div className="hidden sm:block">
          {/* Days of Week Header */}
          <div className="mb-2 grid grid-cols-7 gap-0">
            {weekDates.map((date, index) => {
              const dayDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
              const isToday = dayDate.getTime() === todayDate.getTime();
              return (
                <div
                  key={index}
                  className={`p-2 text-center text-sm font-medium border border-[#2C2731]/20 ${
                    isToday ? 'bg-[#2C2731] text-[#FFFCEF]' : 'bg-[#E8DFBD] text-[#2C2731]/80'
                  }`}
                >
                  <div>{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}</div>
                  <div className="text-lg font-semibold">{date.getDate()}</div>
                </div>
              );
            })}
          </div>

          {/* Week Grid */}
          <div className="grid grid-cols-7 gap-0 border border-[#2C2731]/30">
            {weekDates.map((date, index) => {
              const dayAppointments = getAppointmentsForDay(date);
              const dayDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
              const isToday = dayDate.getTime() === todayDate.getTime();

              return (
                <div
                  key={index}
                  className={`min-h-[200px] border border-[#2C2731]/20 p-2 ${
                    isToday ? 'bg-[#E8DFBD]' : 'bg-[#FFFCEF]'
                  }`}
                >
                  <div className="space-y-1">
                    {dayAppointments.map((apt) => {
                      const joinable = canEnterWaitingRoom(apt);
                      return (
                        <div key={apt.id} className="group">
                          <button
                            onClick={() => {
                              if (joinable) {
                                navigate(`/waiting-room?sessionId=${apt.id}`);
                              }
                            }}
                            disabled={!joinable}
                            className={`text-xs px-1.5 py-1 rounded text-left w-full transition-colors border ${
                              joinable
                                ? 'bg-[#2C2731] text-[#FFFCEF] hover:bg-[#241F2A] cursor-pointer border-[#2C2731]'
                                : 'bg-[#E8DFBD] text-[#2C2731]/70 cursor-not-allowed border-[#2C2731]/30'
                            }`}
                          >
                            <div className="font-medium truncate">{apt.title}</div>
                            <div className="text-[10px] opacity-80">{formatTime(apt.date)}</div>
                            <div className="text-[10px] opacity-80 truncate">{apt.client_name}</div>
                          </button>
                          <div className="hidden group-hover:flex justify-end mt-0.5">
                            <AddToCalendarButton sessionId={apt.id} iconOnly />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const dayAppointments = getAppointmentsForDay(currentDate);

    return (
      <div className="p-2 sm:p-4">
        <div className="space-y-3">
          {dayAppointments.length === 0 ? (
            <div className="text-center py-12 text-[#2C2731]/70 text-sm">
              No appointments scheduled for this day
            </div>
          ) : (
            dayAppointments.map((apt) => {
              const joinable = canEnterWaitingRoom(apt);
              const needsConfirmation = apt.status === 'scheduled' && apt.scheduled_by === 'seeker';

              return (
                <div
                  key={apt.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF] hover:bg-[#F3EBCD] transition-colors"
                >
                  {/* Mobile: Time and client inline */}
                  <div className="flex items-center gap-3 sm:hidden">
                    <div className="flex-shrink-0 text-center min-w-[60px]">
                      <div className="text-sm font-semibold text-[#2C2731]">{formatTime(apt.date)}</div>
                      <div className="text-xs text-[#2C2731]/70">{apt.duration} min</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#2C2731] text-sm truncate">{apt.client_name}</p>
                      <p className="text-xs text-[#2C2731]/70">{apt.title}</p>
                    </div>
                    {joinable && (
                      <button
                        onClick={() => navigate(`/waiting-room?sessionId=${apt.id}`)}
                        className="px-3 py-1.5 text-xs rounded-md bg-[#2C2731] text-[#FFFCEF] flex-shrink-0"
                      >
                        Join
                      </button>
                    )}
                  </div>
                  {needsConfirmation && (
                    <div className="sm:hidden flex items-center gap-2 rounded-md bg-blue-50 border border-blue-200 px-2 py-1.5">
                      <span className="text-xs text-blue-700">Needs confirmation</span>
                      <button
                        onClick={() => handleConfirmSession(apt.id)}
                        disabled={confirmingSessionId === apt.id}
                        className="rounded bg-green-600 px-2 py-0.5 text-xs font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {confirmingSessionId === apt.id ? 'Confirming...' : 'Confirm'}
                      </button>
                    </div>
                  )}

                  {/* Desktop layout */}
                  <div className="hidden sm:flex sm:items-center sm:gap-4 sm:flex-1">
                    <div className="flex-shrink-0 text-center">
                      <div className="text-lg font-semibold text-[#2C2731]">{formatTime(apt.date)}</div>
                      <div className="text-xs text-[#2C2731]/70">{apt.duration} min</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {generateAvatar(apt.client_name)}
                        <div>
                          <p className="font-medium text-[#2C2731] truncate">{apt.client_name}</p>
                          <p className="text-sm text-[#2C2731]/70">{apt.title}</p>
                        </div>
                      </div>
                      {needsConfirmation && (
                        <div className="mt-2 flex items-center gap-2 rounded-md bg-blue-50 border border-blue-200 px-2 py-1.5">
                          <span className="text-xs text-blue-700">Needs confirmation</span>
                          <button
                            onClick={() => handleConfirmSession(apt.id)}
                            disabled={confirmingSessionId === apt.id}
                            className="rounded bg-green-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {confirmingSessionId === apt.id ? 'Confirming...' : 'Confirm'}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <AddToCalendarButton sessionId={apt.id} />
                      {joinable && (
                        <button
                          onClick={() => navigate(`/waiting-room?sessionId=${apt.id}`)}
                          className="px-3 py-1.5 text-sm rounded-md bg-[#2C2731] text-[#FFFCEF] hover:bg-[#241F2A] transition-colors"
                        >
                          Join
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const renderCalendarContent = () => {
    switch (calendarFilter) {
      case 'today':
        return renderDayView();
      case 'week':
        return renderWeekView();
      default:
        return (
          <div className="p-2 sm:p-4">
            {/* Days of Week Header */}
            <div className="mb-1 sm:mb-2 grid grid-cols-7 gap-0">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                <div key={i} className="p-1 sm:p-2 text-center text-xs sm:text-sm font-medium text-[#2C2731]/80 bg-[#E8DFBD] border border-[#2C2731]/20">
                  <span className="sm:hidden">{day}</span>
                  <span className="hidden sm:inline">{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][i]}</span>
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-0 border border-[#2C2731]/30">
              {renderCalendar()}
            </div>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-[#FFFCEF] text-[#2C2731] min-h-full">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-[#2C2731]/70">Loading schedule...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 bg-[#FFFCEF] text-[#2C2731] min-h-full">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-medium font-recoleta text-[#2C2731] mb-1">Schedule</h1>
              <p className="text-xs sm:text-sm text-[#2C2731]/80 hidden sm:block">Manage your appointments and availability</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* View Toggle */}
              <div className="flex items-center">
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`px-2 sm:px-3 py-1.5 text-sm rounded-l-md border border-[#2C2731] transition-colors ${viewMode === 'calendar' ? 'bg-[#2C2731] text-[#FFFCEF]' : 'bg-[#FFFCEF] text-[#2C2731] hover:bg-[#F3EBCD]'}`}
                >
                  <span className="flex items-center gap-1">
                    <CalendarIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">Calendar</span>
                  </span>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-2 sm:px-3 py-1.5 text-sm rounded-r-md border border-[#2C2731] border-l-0 transition-colors ${viewMode === 'list' ? 'bg-[#2C2731] text-[#FFFCEF]' : 'bg-[#FFFCEF] text-[#2C2731] hover:bg-[#F3EBCD]'}`}
                >
                  <span className="flex items-center gap-1">
                    <ListBulletIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">List</span>
                  </span>
                </button>
              </div>

              {/* Working Hours Button */}
              <button
                onClick={() => setShowWorkingHours(!showWorkingHours)}
                className={`px-2 sm:px-3 py-1.5 text-sm rounded-md border border-[#2C2731] transition-colors ${showWorkingHours ? 'bg-[#2C2731] text-[#FFFCEF]' : 'bg-[#FFFCEF] text-[#2C2731] hover:bg-[#F3EBCD]'}`}
              >
                <span className="flex items-center gap-1">
                  <Cog6ToothIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Consultation Call Hours</span>
                </span>
              </button>
            </div>
          </div>

          {/* Legend - hidden on mobile */}
          <div className="hidden md:flex items-center space-x-3 text-sm text-[#2C2731] mt-3">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-[#2C2731] rounded-full"></div>
              <span>Client Appointment</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-[#E8DFBD] border border-[#2C2731] rounded-full"></div>
              <span>Consultation</span>
            </div>
          </div>
        </div>

        {/* Calendar Connection Alert */}
        {calendarConnected === false && !calendarAlertDismissed && (
          <div className="mb-4 sm:mb-6 rounded-lg border border-amber-500 bg-amber-50 p-3 sm:p-4">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="ml-2 sm:ml-3 flex-1 min-w-0">
                <h3 className="text-sm font-medium text-amber-800">
                  No calendar connected
                </h3>
                <p className="mt-1 text-xs sm:text-sm text-amber-700 hidden sm:block">
                  Connect your Google Calendar or Outlook Calendar to sync your availability and avoid double-bookings.
                </p>
                <div className="mt-2 sm:mt-3">
                  <button
                    onClick={() => {
                      window.location.hash = 'settings-calendar';
                    }}
                    className="inline-flex items-center rounded-md bg-amber-600 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-white hover:bg-amber-700 transition-colors"
                  >
                    <CalendarIcon className="h-4 w-4 mr-1 sm:mr-1.5" />
                    Connect
                  </button>
                </div>
              </div>
              <button
                onClick={() => setCalendarAlertDismissed(true)}
                className="ml-2 sm:ml-4 flex-shrink-0 rounded p-1 text-amber-500 hover:bg-amber-100 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Working Hours Panel */}
        {showWorkingHours && (
          <div className="mb-6 overflow-hidden rounded-lg border border-[#2C2731] bg-[#FFFCEF]">
            <div className="border-b border-[#2C2731] px-6 py-4 bg-[#2C2731] text-[#FFFCEF]">
              <h2 className="text-lg font-medium font-recoleta">Consultation Call Hours</h2>
              <p className="text-sm text-[#FFFCEF]/80">Set your availability for consultation calls with new seekers</p>
            </div>
            <div className="p-6">
              <WorkingHoursEditor
                onSave={() => setShowWorkingHours(false)}
                showSaveButton={true}
              />
            </div>
          </div>
        )}

        {viewMode === 'calendar' ? (
          <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-4">
            {/* Calendar Section */}
            <div className="overflow-hidden rounded-lg border border-[#2C2731] bg-[#FFFCEF] lg:col-span-3">
              {/* Calendar Header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-[#2C2731] bg-[#2C2731] px-3 sm:px-6 py-3 sm:py-4 text-[#FFFCEF] gap-2 sm:gap-0">
                <div className="flex items-center justify-between sm:justify-start sm:space-x-4">
                  <button
                    onClick={() => navigate_calendar('prev')}
                    className="rounded p-1 hover:bg-[#4B4353] transition-colors"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <h2 className="text-sm sm:text-lg font-medium min-w-0 sm:min-w-[200px] text-center truncate">{getHeaderText()}</h2>
                  <button
                    onClick={() => navigate_calendar('next')}
                    className="rounded p-1 hover:bg-[#4B4353] transition-colors"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </div>
                <div className="flex items-center justify-center sm:justify-end space-x-1 sm:space-x-2 text-xs sm:text-sm">
                  <button
                    onClick={goToToday}
                    className={`px-2 sm:px-3 py-1 rounded transition-colors ${
                      calendarFilter === 'today'
                        ? 'bg-[#FFFCEF] text-[#2C2731]'
                        : 'text-[#FFFCEF]/80 hover:bg-[#4B4353]'
                    }`}
                  >
                    Today
                  </button>
                  <button
                    onClick={() => setCalendarFilter('week')}
                    className={`px-2 sm:px-3 py-1 rounded transition-colors ${
                      calendarFilter === 'week'
                        ? 'bg-[#FFFCEF] text-[#2C2731]'
                        : 'text-[#FFFCEF]/80 hover:bg-[#4B4353]'
                    }`}
                  >
                    Week
                  </button>
                  <button
                    onClick={() => setCalendarFilter('month')}
                    className={`px-2 sm:px-3 py-1 rounded transition-colors ${
                      calendarFilter === 'month'
                        ? 'bg-[#FFFCEF] text-[#2C2731]'
                        : 'text-[#FFFCEF]/80 hover:bg-[#4B4353]'
                    }`}
                  >
                    Month
                  </button>
                </div>
              </div>

              {/* Calendar Content */}
              {renderCalendarContent()}
            </div>

            {/* Sidebar - hidden on mobile, shown on desktop */}
            <div className="hidden lg:block space-y-6">
              {/* Today's Appointments */}
              <div className="overflow-hidden rounded-lg border border-[#2C2731] bg-[#FFFCEF]">
                <div className="border-b border-[#2C2731] px-4 py-3 bg-[#2C2731] text-[#FFFCEF]">
                  <h3 className="text-base font-medium font-recoleta">Today's Appointments</h3>
                  <p className="text-xs text-[#FFFCEF]/80">
                    {new Date().toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </p>
                </div>
                <div className="space-y-3 p-4 text-[#2C2731]">
                  {getTodayAppointments().map((apt) => {
                    const joinable = canEnterWaitingRoom(apt);
                    const needsConfirmation = apt.status === 'scheduled' && apt.scheduled_by === 'seeker';
                    return (
                      <div key={apt.id} className="rounded-lg p-2 -m-2 group">
                        <div className="flex items-start gap-3">
                          <button
                            onClick={() => {
                              if (joinable) {
                                navigate(`/waiting-room?sessionId=${apt.id}`);
                              }
                            }}
                            disabled={!joinable}
                            className={`flex items-center space-x-3 flex-1 text-left transition-colors ${
                              joinable ? 'hover:bg-[#F3EBCD] cursor-pointer' : 'cursor-not-allowed opacity-70'
                            }`}
                          >
                            {generateAvatar(apt.client_name)}
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-[#2C2731]">{apt.client_name}</p>
                              <p className="truncate text-xs text-[#2C2731]/70">{apt.title}</p>
                              <div className="mt-1 flex items-center space-x-2">
                                <div className="flex items-center text-xs text-[#2C2731]/70">
                                  {getTypeIcon(apt.type)}
                                  <span className="ml-1">{formatTime(apt.date)}</span>
                                </div>
                              </div>
                            </div>
                          </button>
                          <AddToCalendarButton sessionId={apt.id} iconOnly />
                        </div>
                        {needsConfirmation && (
                          <div className="mt-2 flex items-center gap-2 rounded-md bg-blue-50 border border-blue-200 px-2 py-1.5">
                            <span className="text-xs text-blue-700">Needs confirmation</span>
                            <button
                              onClick={() => handleConfirmSession(apt.id)}
                              disabled={confirmingSessionId === apt.id}
                              className="rounded bg-green-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {confirmingSessionId === apt.id ? 'Confirming...' : 'Confirm'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {getTodayAppointments().length === 0 && (
                    <p className="text-sm text-[#2C2731]/70">No appointments today</p>
                  )}
                </div>
              </div>

              {/* Upcoming This Week */}
              <div className="overflow-hidden rounded-lg border border-[#2C2731] bg-[#FFFCEF]">
                <div className="border-b border-[#2C2731] px-4 py-3 bg-[#2C2731] text-[#FFFCEF]">
                  <h3 className="text-base font-medium font-recoleta">Upcoming This Week</h3>
                </div>
                <div className="space-y-4 p-4 text-[#2C2731]">
                  {getUpcomingWeekAppointments().slice(0, 3).map((apt) => {
                    const joinable = canEnterWaitingRoom(apt);
                    const needsConfirmation = apt.status === 'scheduled' && apt.scheduled_by === 'seeker';
                    return (
                      <div key={apt.id} className="rounded-lg p-2 -m-2">
                        <div className="flex items-start gap-2">
                          <button
                            onClick={() => {
                              if (joinable) {
                                navigate(`/waiting-room?sessionId=${apt.id}`);
                              }
                            }}
                            disabled={!joinable}
                            className={`space-y-2 flex-1 text-left transition-colors ${
                              joinable ? 'hover:bg-[#F3EBCD] cursor-pointer' : 'cursor-not-allowed opacity-70'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-[#2C2731]">{apt.client_name}</p>
                              <div className={`h-2 w-2 rounded-full ${joinable ? 'bg-[#2C2731]' : 'bg-[#E8DFBD]'}`}></div>
                            </div>
                            <p className="text-xs text-[#2C2731]/80">{formatDate(apt.date)}</p>
                            <p className="text-xs text-[#2C2731]/70">{formatTime(apt.date)}</p>
                          </button>
                          <AddToCalendarButton sessionId={apt.id} iconOnly />
                        </div>
                        {needsConfirmation && (
                          <div className="mt-2 flex items-center gap-2 rounded-md bg-blue-50 border border-blue-200 px-2 py-1.5">
                            <span className="text-xs text-blue-700">Needs confirmation</span>
                            <button
                              onClick={() => handleConfirmSession(apt.id)}
                              disabled={confirmingSessionId === apt.id}
                              className="rounded bg-green-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {confirmingSessionId === apt.id ? 'Confirming...' : 'Confirm'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <div className="border-t border-[#2C2731]/30 pt-2">
                    <button className="text-xs text-[#2C2731] hover:text-[#241F2A] transition-colors">
                      View All Appointments
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile: Upcoming sessions below calendar */}
            <div className="lg:hidden space-y-4">
              {/* Today's Appointments - Mobile */}
              <div className="overflow-hidden rounded-lg border border-[#2C2731] bg-[#FFFCEF]">
                <div className="border-b border-[#2C2731] px-3 py-2 bg-[#2C2731] text-[#FFFCEF]">
                  <h3 className="text-sm font-medium">Today</h3>
                </div>
                <div className="p-3 text-[#2C2731]">
                  {getTodayAppointments().length > 0 ? (
                    <div className="space-y-2">
                      {getTodayAppointments().map((apt) => {
                        const joinable = canEnterWaitingRoom(apt);
                        const needsConfirmation = apt.status === 'scheduled' && apt.scheduled_by === 'seeker';
                        return (
                          <div key={apt.id} className="flex items-center gap-3 p-2 rounded-md bg-[#F3EBCD]">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#2C2731] truncate">{apt.client_name}</p>
                              <p className="text-xs text-[#2C2731]/70">{formatTime(apt.date)} · {apt.title}</p>
                              {needsConfirmation && (
                                <button
                                  onClick={() => handleConfirmSession(apt.id)}
                                  disabled={confirmingSessionId === apt.id}
                                  className="mt-1 rounded bg-green-600 px-2 py-0.5 text-xs font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {confirmingSessionId === apt.id ? 'Confirming...' : 'Confirm'}
                                </button>
                              )}
                            </div>
                            {joinable && (
                              <button
                                onClick={() => navigate(`/waiting-room?sessionId=${apt.id}`)}
                                className="px-3 py-1.5 text-xs rounded-md bg-[#2C2731] text-[#FFFCEF] flex-shrink-0"
                              >
                                Join
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-[#2C2731]/70 text-center py-2">No appointments today</p>
                  )}
                </div>
              </div>

              {/* Upcoming This Week - Mobile */}
              {getUpcomingWeekAppointments().length > 0 && (
                <div className="overflow-hidden rounded-lg border border-[#2C2731] bg-[#FFFCEF]">
                  <div className="border-b border-[#2C2731] px-3 py-2 bg-[#2C2731] text-[#FFFCEF]">
                    <h3 className="text-sm font-medium">Upcoming This Week</h3>
                  </div>
                  <div className="p-3 text-[#2C2731]">
                    <div className="space-y-2">
                      {getUpcomingWeekAppointments().slice(0, 5).map((apt) => {
                        const joinable = canEnterWaitingRoom(apt);
                        const needsConfirmation = apt.status === 'scheduled' && apt.scheduled_by === 'seeker';
                        return (
                          <div key={apt.id} className="flex items-center gap-3 p-2 rounded-md border border-[#2C2731]/20">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#2C2731] truncate">{apt.client_name}</p>
                              <p className="text-xs text-[#2C2731]/70">{formatDate(apt.date)} · {formatTime(apt.date)}</p>
                              {needsConfirmation && (
                                <button
                                  onClick={() => handleConfirmSession(apt.id)}
                                  disabled={confirmingSessionId === apt.id}
                                  className="mt-1 rounded bg-green-600 px-2 py-0.5 text-xs font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {confirmingSessionId === apt.id ? 'Confirming...' : 'Confirm'}
                                </button>
                              )}
                            </div>
                            {joinable && (
                              <button
                                onClick={() => navigate(`/waiting-room?sessionId=${apt.id}`)}
                                className="px-2 py-1 text-xs rounded-md bg-[#2C2731] text-[#FFFCEF] flex-shrink-0"
                              >
                                Join
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <ScheduleListView appointments={appointments} onConfirm={handleConfirmSession} confirmingSessionId={confirmingSessionId} guideTimezone={guideTimezone} />
        )}
      </div>
    </div>
  );
};

export default SchedulePage;
